"use client";

import React, { useState } from 'react';
import NextImage from 'next/image';
import { Plus, ShoppingBag, Loader2, Phone, User, Check } from 'lucide-react';

import { EditorField } from '@/config/ecoBuilder/types';
import { cn } from '@/lib/utils';
import { useForm, SubmitHandler } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import * as z from 'zod';
import { toast } from "sonner";
import { createOrder } from "@/actions/order-actions";
import { usePageContext } from "@/components/ecoBuilder/renderer/PageContext";
import { ALGERIAN_WILAYAS } from '@/lib/data/wilayas';
import { OFFICIAL_COMMUNES } from '@/lib/data/official-locations';

import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue
} from "@/components/ecoBuilder/ui/select";
import { Button } from "@/components/ecoBuilder/ui/button";
import { Input } from "@/components/ecoBuilder/ui/input";


import { RichTextRenderer } from '@/components/ecoBuilder/atoms/RichTextRenderer';

// ------------------------------------------------------------------
// Types & Schema
// ------------------------------------------------------------------

// ... (Keep existing top imports)
// NOTE: Reverted to manual inputs because 'product' type is not supported in arrays yet.

// ... imports
import { createClient } from "@/utils/supabase/client";

// 36. Update Hotspot Interface
export interface Hotspot {
    id: string;
    x: number;
    y: number;
    productId?: string;
}

const phoneRegex = /^(05|06|07)[0-9]{8}$/;

export interface ShoppableLookbookProps {
    id: string;
    title?: string;
    subtitle?: string;
    image?: string;
    hotspots: Hotspot[];
    hotspotColor?: string;
    hotspotIconColor?: string;
    hotspotAnimation?: "pulse" | "static";
    productPriceColor?: string;
    submitButtonColor?: string;
    submitButtonTextColor?: string;
    paddingTop?: number;
    paddingBottom?: number;
    isSelected?: boolean;
}

// ...

// 63. Update Schema
export const ShoppableLookbookSchema: EditorField[] = [
    // ... (Keep title, subtitle, image)
    {
        key: "title",
        label: "العنوان الرئيسي",
        type: "richtext",
        defaultValue: "تسوقي هذا المظهر"
    },
    {
        key: "subtitle",
        label: "العنوان الفرعي",
        type: "richtext",
        defaultValue: "اضغطي على المنتجات للشراء المباشر"
    },
    {
        key: "image",
        label: "الصورة الرئيسية",
        type: "image",
        description: "يفضل صورة طولية أو مربعة عالية الدقة"
    },
    {
        key: "hotspots",
        label: "نقاط المنتجات (Hotspots)",
        type: "array",
        arrayConfig: {
            itemLabel: "منتج",
            schema: [
                {
                    key: "x",
                    label: "موقع أفقي (X%)",
                    type: "number",
                    defaultValue: 50
                },
                {
                    key: "y",
                    label: "موقع عمودي (Y%)",
                    type: "number",
                    defaultValue: 50
                },
                {
                    key: "productId",
                    label: "اختر المنتج",
                    type: "product"
                }
            ]
        },
        defaultValue: [
            { id: '1', x: 30, y: 40 },
            { id: '2', x: 60, y: 70 }
        ]
    },
    // ... (Keep colors etc)
    {
        key: "hotspotColor",
        label: "لون النقاط",
        type: "color"
    },
    {
        key: "hotspotIconColor",
        label: "لون الأيقونة (+)",
        type: "color"
    },
    {
        key: "productPriceColor",
        label: "لون السعر",
        type: "color"
    },
    {
        key: "submitButtonColor",
        label: "لون زر الطلب",
        type: "color"
    },
    {
        key: "submitButtonTextColor",
        label: "لون نص زر الطلب",
        type: "color"
    },
    {
        key: "hotspotAnimation",
        label: "حركة النقاط",
        type: "select",
        options: [
            { label: "نبض (Pulse)", value: "pulse" },
            { label: "ثابت (Static)", value: "static" }
        ],
        defaultValue: "pulse"
    },
    {
        key: "paddingTop",
        label: "مسافة علوية",
        type: "slider",
        defaultValue: 40,
        sliderConfig: { min: 0, max: 100, step: 4, unit: "px" }
    },
    {
        key: "paddingBottom",
        label: "مسافة سفلية",
        type: "slider",
        defaultValue: 40,
        sliderConfig: { min: 0, max: 100, step: 4, unit: "px" }
    }
];

// ... (KEEP DEFAULT PROPS but update structure)
export const ShoppableLookbookDefaultProps = {
    title: "تسوقي هذا المظهر",
    subtitle: "اضغطي على المنتجات للشراء المباشر",
    image: "",
    hotspots: [
        { id: '1', x: 30, y: 40, productTitle: 'نظارة شمسية', productPrice: '1500' },
        { id: '2', x: 60, y: 70, productTitle: 'حقيبة جلدية', productPrice: '3500' }
    ],
    hotspotColor: "#ffffff",
    hotspotIconColor: "#000000",
    productPriceColor: "#10b981",
    submitButtonColor: "#000000",
    submitButtonTextColor: "#ffffff",
    hotspotAnimation: "pulse",
    paddingTop: 40,
    paddingBottom: 40
};


// 370. Update HotspotMarker to fetch product data
const HotspotMarker = ({
    spot, // Contains { x, y, productId... }
    color,
    iconColor,
    priceColor,
    submitButtonColor,
    submitButtonTextColor,
    animation
}: {
    spot: Hotspot;
    color: string;
    iconColor?: string;
    priceColor?: string;
    submitButtonColor?: string;
    submitButtonTextColor?: string;
    animation: "pulse" | "static"
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [productData, setProductData] = useState<any>(null);
    const [loading, setLoading] = useState(false);

    // FETCH EFFECT
    React.useEffect(() => {
        let isMounted = true;

        const fetchProduct = async () => {
            if (!spot.productId) return;

            setLoading(true);
            const supabase = createClient();
            const { data } = await supabase.from('products').select('*').eq('id', spot.productId).single();

            if (isMounted) {
                if (data) setProductData(data);
                setLoading(false);
            }
        };

        // Only fetch if open and no data yet
        if (isOpen && !productData && spot.productId) {
            fetchProduct();
        }
    }, [isOpen, spot.productId, productData]);

    // Derived Data
    const title = productData?.name || (loading ? "جاري التحميل..." : "منتج");
    const price = productData?.price || 0;
    const image = productData?.images?.[0] || productData?.image_url || null;

    return (
        <Popover open={isOpen} onOpenChange={setIsOpen}>
            <PopoverTrigger asChild>
                <button
                    className="absolute w-8 h-8 -ml-4 -mt-4 rounded-full flex items-center justify-center shadow-lg transition-transform hover:scale-110 active:scale-95 focus:outline-none z-20 group"
                    style={{
                        left: `${spot.x}%`,
                        top: `${spot.y}%`,
                        backgroundColor: color,
                        color: iconColor || (isLight(color) ? 'black' : 'white')
                    }}
                >
                    {animation === 'pulse' && (
                        <span
                            className="absolute inset-0 rounded-full animate-ping opacity-50"
                            style={{ backgroundColor: color }}
                        />
                    )}
                    <Plus size={16} strokeWidth={3} className="relative z-10 transition-transform group-hover:rotate-45" />
                </button>
            </PopoverTrigger>
            <PopoverContent
                side="top"
                sideOffset={10}
                className="w-72 p-0 overflow-hidden rounded-xl border-none shadow-xl z-50"
            >
                <div className="flex flex-col bg-white dark:bg-zinc-900 border border-border">
                    {/* Product Header */}
                    <div className="flex p-3 gap-3 border-b border-border/50 bg-muted/20">
                        <div className="relative w-16 h-16 rounded-lg overflow-hidden bg-white shrink-0 border border-border/50">
                            {loading ? (
                                <div className="w-full h-full flex items-center justify-center bg-muted"><Loader2 className="animate-spin w-4 h-4" /></div>
                            ) : image ? (
                                <NextImage
                                    src={image}
                                    alt={title}
                                    fill
                                    className="object-cover"
                                    unoptimized
                                />
                            ) : (
                                <div className="w-full h-full flex items-center justify-center text-muted-foreground bg-muted"><ShoppingBag size={16} /></div>
                            )}
                        </div>
                        <div className="flex flex-col justify-center gap-1" dir="rtl">
                            <h4 className="font-bold text-sm leading-tight text-right line-clamp-2">
                                {title}
                            </h4>
                            <p className="text-sm font-bold text-right" style={{ color: priceColor }}>
                                {loading ? "..." : (price > 0 ? `${price.toLocaleString()} ${productData?.currency || 'DZD'}` : "غير محدد")}
                            </p>
                        </div>
                    </div>

                    {/* Quick Order Form */}
                    <div className="p-3 bg-white dark:bg-zinc-900" dir="rtl">
                        {!spot.productId ? (
                            <div className="text-center text-xs text-muted-foreground py-4">يرجى ربط منتج من الإعدادات</div>
                        ) : !productData && !loading ? (
                            <div className="text-center text-xs text-muted-foreground py-4">لم يتم العثور على المنتج</div>
                        ) : (
                            <>
                                <div className="text-xs text-muted-foreground mb-2 text-right">أكمل بياناتك للطلب الفوري:</div>
                                <QuickOrderForm
                                    productData={{
                                        ...productData,
                                        id: spot.productId
                                    }}
                                    onSuccess={() => setIsOpen(false)}
                                    submitButtonColor={submitButtonColor}
                                    submitButtonTextColor={submitButtonTextColor}
                                />
                            </>
                        )}
                    </div>
                </div>
            </PopoverContent>
        </Popover>
    );
};

// Update QuickOrderForm to accept proper product data
const QuickOrderForm = ({
    productData,

    submitButtonColor,
    submitButtonTextColor
}: {
    productData: any, // Contains database product
    onSuccess: () => void,
    submitButtonColor?: string,
    submitButtonTextColor?: string
}) => {
    // ... (Hooks)
    const { siteId } = usePageContext();
    // const { siteSlug } = useSiteContext();
    // const router = useRouter();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isSuccess, setIsSuccess] = useState(false);


    const schema = z.object({
        fullName: z.string().min(2, 'الاسم مطلوب'),
        phone: z.string().regex(phoneRegex, 'رقم الهاتف غير صحيح'),
        wilaya: z.string().min(1, 'يرجى اختيار الولاية'),
        commune: z.string().optional(),
    });

    const { register, handleSubmit, setValue, watch, formState: { errors } } = useForm<z.infer<typeof schema>>({
        resolver: zodResolver(schema),
        defaultValues: {
            fullName: "",
            phone: "",
            wilaya: "",
            commune: ""
        }
    });

    const onSubmit: SubmitHandler<z.infer<typeof schema>> = async (data) => {
        if (!siteId) {
            toast.error("هذا النموذج يعمل فقط في الموقع المباشر");
            return;
        }

        setIsSubmitting(true);
        try {
            const price = productData?.price || 0;
            const finalImage = productData?.images?.[0] || productData?.image_url || "/placeholder.png";

            const orderPayload = {
                customerName: data.fullName,
                customerPhone: data.phone,
                customerWilaya: data.wilaya,
                customerAddress: "طلب سريع من Lookbook",
                customerCommune: data.commune || '',
                items: [{
                    id: productData.id || 'quick-lookbook-item',
                    name: productData.name || "منتج",
                    price: price,
                    quantity: 1,
                    image: finalImage
                }],
                subtotal: price,
                shippingCost: 0,
                total: price,
                paymentMethod: 'cod' as const,
                siteId: siteId
            };

            const result = await createOrder(orderPayload);
            // ... (keep existing success logic)
            if (result.success) {
                toast.success("تم الطلب بنجاح!");
                // onSuccess(); // Keep popover open to show success message
                setIsSuccess(true);
            } else {
                toast.error(result.error || "خطأ في الطلب");
            }
        } catch (e) {
            console.error(e);
            toast.error("حدث خطأ غير متوقع");
        } finally {
            setIsSubmitting(false);
        }
    };

    // ... (Return Form JSX - mostly unchanged except props usage)
    return (
        isSuccess ? (
            <div className="p-4 text-center animate-in fade-in zoom-in duration-300">
                <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <Check className="w-6 h-6 text-green-600" />
                </div>
                <h3 className="font-bold text-green-800 mb-1">شكراً لك!</h3>
                <p className="text-xs text-green-700">تم الطلب بنجاح.</p>
            </div>
        ) : (
            <form onSubmit={handleSubmit(onSubmit)} className="space-y-3 p-1">
                <div className="space-y-1">
                    <div className="relative">
                        <Input {...register('fullName')} placeholder="الاسم الكامل" className="h-9 text-xs text-right pr-2" />
                        <User className="absolute left-2 top-2.5 w-3 h-3 text-muted-foreground/50 pointer-events-none" />
                    </div>
                    {errors.fullName && <p className="text-[10px] text-red-500">{errors.fullName.message}</p>}
                </div>

                <div className="space-y-1">
                    <div className="relative">
                        <Input {...register('phone')} placeholder="رقم الهاتف" className="h-9 text-xs text-right pr-2" dir="rtl" />
                        <Phone className="absolute left-2 top-2.5 w-3 h-3 text-muted-foreground/50 pointer-events-none" />
                    </div>
                    {errors.phone && <p className="text-[10px] text-red-500">{errors.phone.message}</p>}
                </div>

                <div className="grid grid-cols-2 gap-2">
                    <div className="space-y-1">
                        <Select onValueChange={(val) => {
                            setValue('wilaya', val);
                            setValue('commune', '');
                        }}>
                            <SelectTrigger className="h-9 text-xs text-right dir-rtl w-full bg-background border-input">
                                <SelectValue placeholder="الولاية" />
                            </SelectTrigger>
                            <SelectContent className="max-h-[200px]" dir="rtl">
                                {ALGERIAN_WILAYAS.map((w) => (
                                    <SelectItem key={w.code} value={w.code} className="text-right flex-row-reverse justify-start">
                                        <span className="ml-2">{w.code}</span> - {w.name}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                        {errors.wilaya && <p className="text-[10px] text-red-500">{errors.wilaya.message}</p>}
                    </div>

                    <div className="space-y-1">
                        <Select
                            onValueChange={(val) => setValue('commune', val)}
                            disabled={!watch('wilaya')}
                        >
                            <SelectTrigger className="h-9 text-xs text-right dir-rtl w-full bg-background border-input" disabled={!watch('wilaya')}>
                                <SelectValue placeholder="البلدية" />
                            </SelectTrigger>
                            <SelectContent className="max-h-[200px]" dir="rtl">
                                {watch('wilaya') && OFFICIAL_COMMUNES[parseInt(watch('wilaya')!).toString()]?.map((commune) => (
                                    <SelectItem key={commune} value={commune} className="text-right flex-row-reverse justify-start">
                                        {commune}
                                    </SelectItem>
                                ))}
                            </SelectContent>
                        </Select>
                    </div>
                </div>

                <Button
                    type="submit"
                    className="w-full h-9 text-xs font-bold mt-2"
                    disabled={isSubmitting}
                    style={{
                        backgroundColor: submitButtonColor,
                        color: submitButtonTextColor
                    }}
                >
                    {isSubmitting ? <Loader2 className="w-3 h-3 animate-spin" /> : "تأكيد الطلب"}
                </Button>
            </form>
        )
    );
};


// ------------------------------------------------------------------
// Main Component
// ------------------------------------------------------------------

export const ShoppableLookbook = ({
    id,
    title,
    subtitle,
    image,
    hotspots = [],
    hotspotColor = "#ffffff",
    hotspotIconColor = "#000000",
    productPriceColor = "#10b981",
    submitButtonColor = "#000000",
    submitButtonTextColor = "#ffffff",
    hotspotAnimation = "pulse",
    paddingTop = 40,
    paddingBottom = 40
}: ShoppableLookbookProps) => {

    return (
        <section
            id={id}
            className={cn(
                "w-full relative overflow-hidden"
            )
            }
            style={{ paddingTop, paddingBottom }}
        >
            <div className="container mx-auto px-4">

                {/* Headers */}
                {(title || subtitle) && (
                    <div className="text-center mb-8 space-y-2">
                        {title && <h2 className="text-2xl md:text-3xl font-bold"><RichTextRenderer html={title} /></h2>}
                        {subtitle && <div className="text-muted-foreground"><RichTextRenderer html={subtitle} /></div>}
                        <div className="w-16 h-1 bg-primary/20 mx-auto rounded-full mt-4" />
                    </div>
                )}

                <div className="relative w-full max-w-4xl mx-auto rounded-2xl overflow-hidden shadow-2xl bg-muted aspect-[4/5] md:aspect-[16/9] lg:aspect-[21/9]">
                    {image ? (
                        <NextImage
                            src={image}
                            alt="Lookbook"
                            fill
                            className="object-cover"
                            unoptimized
                        />
                    ) : (
                        <div className="w-full h-full flex items-center justify-center bg-zinc-100 text-zinc-400">
                            <div className="flex flex-col items-center gap-2">
                                <ShoppingBag size={48} className="opacity-50" />
                                <span>أضف صورة للعرض</span>
                            </div>
                        </div>
                    )}

                    {/* Hotspots Layer */}
                    {hotspots.map((spot, index) => (
                        <HotspotMarker
                            key={spot.id || index}
                            spot={spot}
                            color={hotspotColor}
                            iconColor={hotspotIconColor}
                            priceColor={productPriceColor}
                            submitButtonColor={submitButtonColor}
                            submitButtonTextColor={submitButtonTextColor}
                            animation={hotspotAnimation}
                        />
                    ))}
                </div>
            </div>
        </section>
    );
};

// Helper to check if color is light (for text contrast)
function isLight(color: string) {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    const yiq = ((r * 299) + (g * 587) + (b * 114)) / 1000;
    return yiq >= 128;
}
